import { useState, useEffect, useMemo } from "react";
import {
  Info,
  Search,
  CheckCircle2,
  Lock,
  Save,
  RotateCcw,
  Shield,
  LayoutDashboard,
  Search as SearchIcon,
  Clock,
  UserCheck,
  Globe,
  Bell,
  Trash2,
  FileCode,
  CreditCard,
  Settings,
  ShieldCheck,
  GitBranch,
  Activity,
  Radar,
  FileSpreadsheet,
  Key,
} from "lucide-react";
import { toast } from "sonner";
import { fetchPermissionMatrix, savePermissionMatrix } from "../../../api/accessApi";

interface FeatureDef {
  id: string;
  name: string;
  category: "Core Features" | "Monitoring & Protection" | "Admin & Governance" | "Exports & APIs";
  desc: string;
  icon: React.ReactNode;
}

const FEATURES: FeatureDef[] = [
  // Core Features
  { id: "dashboard", name: "Dashboard", category: "Core Features", desc: "Main threat intelligence overview & metrics", icon: <LayoutDashboard size={16} /> },
  { id: "intelligence_feed", name: "Intelligence Feed", category: "Core Features", desc: "Darkweb breach database search & leaks", icon: <SearchIcon size={16} /> },
  { id: "timeline", name: "Timeline", category: "Core Features", desc: "Historical breach event timeline visualization", icon: <Clock size={16} /> },
  { id: "customer_check", name: "Customer Check", category: "Core Features", desc: "Domain & user compromised credential check", icon: <UserCheck size={16} /> },

  // Monitoring & Protection
  { id: "domain_monitor", name: "Domain Monitor", category: "Monitoring & Protection", desc: "Monitored domains watchlists & alerts", icon: <Globe size={16} /> },
  { id: "alert_config", name: "Alert Config", category: "Monitoring & Protection", desc: "Instant alert webhooks & notifications", icon: <Bell size={16} /> },
  { id: "takedowns", name: "Takedowns", category: "Monitoring & Protection", desc: "Request takedowns & track request status", icon: <Trash2 size={16} /> },
  { id: "review", name: "Review", category: "Monitoring & Protection", desc: "Sample uploads & malware analysis review", icon: <FileCode size={16} /> },

  // Admin & Governance
  { id: "admin_dashboard", name: "Admin Dashboard", category: "Admin & Governance", desc: "Executive admin stats & system health", icon: <Settings size={16} /> },
  { id: "access_management", name: "Access Console", category: "Admin & Governance", desc: "User Directory & Role Matrix management", icon: <ShieldCheck size={16} /> },
  { id: "engagement_board", name: "Engagement Board", category: "Admin & Governance", desc: "Client engagements & analyst workflows", icon: <GitBranch size={16} /> },
  { id: "audit_logs", name: "Audit Logs", category: "Admin & Governance", desc: "System audit logs & compliance trail", icon: <Activity size={16} /> },
  { id: "forum_monitoring", name: "Forum Monitoring", category: "Admin & Governance", desc: "Forum scrapers & operational run statuses", icon: <Radar size={16} /> },

  // Exports & APIs
  { id: "subscription", name: "Subscription", category: "Exports & APIs", desc: "Subscription plans & tier upgrades", icon: <CreditCard size={16} /> },
  { id: "reports_export", name: "Reports & CSV", category: "Exports & APIs", desc: "Generate PDF reports & CSV data exports", icon: <FileSpreadsheet size={16} /> },
  { id: "api_access", name: "API Access", category: "Exports & APIs", desc: "Programmatic REST API keys & webhooks", icon: <Key size={16} /> },
];

export default function PermissionMatrix() {
  const [initialMatrix, setInitialMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [stagedMatrix, setStagedMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [initialShowLocked, setInitialShowLocked] = useState<boolean>(true);
  const [stagedShowLocked, setStagedShowLocked] = useState<boolean>(true);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const loadMatrix = async () => {
    try {
      setIsLoading(true);
      const res = await fetchPermissionMatrix();
      setInitialMatrix(res.matrix);
      setStagedMatrix(JSON.parse(JSON.stringify(res.matrix)));
      const showLocked = res.settings?.show_locked_tabs ?? true;
      setInitialShowLocked(showLocked);
      setStagedShowLocked(showLocked);
    } catch (err: unknown) {
      toast.error("Failed to load permission matrix");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, []);

  const diffs = useMemo(() => {
    const changes: Array<{
      feature: string;
      role: string;
      from: boolean;
      to: boolean;
      isSetting?: boolean;
    }> = [];
    if (!initialMatrix || !stagedMatrix) return changes;

    for (const feat of FEATURES) {
      const fid = feat.id;
      for (const role of ["client", "analyst", "admin"]) {
        const fromVal = initialMatrix[fid]?.[role] ?? false;
        const toVal = stagedMatrix[fid]?.[role] ?? false;
        if (fromVal !== toVal) {
          changes.push({
            feature: feat.name,
            role,
            from: fromVal,
            to: toVal,
          });
        }
      }
    }

    if (stagedShowLocked !== initialShowLocked) {
      changes.push({
        feature: "Restricted Navigation Display",
        role: "Global Sidebar Setting",
        from: initialShowLocked,
        to: stagedShowLocked,
        isSetting: true,
      });
    }

    return changes;
  }, [initialMatrix, stagedMatrix, stagedShowLocked, initialShowLocked]);

  const handleToggle = (featId: string, role: string) => {
    // Guardrails check
    if (role === "admin" && (featId === "access_management" || featId === "admin_dashboard")) {
      toast.error("Admin role must retain Access Console and Admin Dashboard access");
      return;
    }
    if (featId === "dashboard") {
      toast.error("Dashboard feature cannot be disabled for any role");
      return;
    }

    setStagedMatrix((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      if (!copy[featId]) copy[featId] = {};
      copy[featId][role] = !copy[featId][role];
      return copy;
    });
  };

  const handleDiscard = () => {
    setStagedMatrix(JSON.parse(JSON.stringify(initialMatrix)));
    setStagedShowLocked(initialShowLocked);
    toast.info("Staged changes discarded");
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);
      const res = await savePermissionMatrix(stagedMatrix, {
        show_locked_tabs: stagedShowLocked,
      });
      setInitialMatrix(res.matrix);
      setStagedMatrix(JSON.parse(JSON.stringify(res.matrix)));
      const savedShowLocked = res.settings?.show_locked_tabs ?? stagedShowLocked;
      setInitialShowLocked(savedShowLocked);
      setStagedShowLocked(savedShowLocked);
      setShowConfirmModal(false);
      window.dispatchEvent(new CustomEvent("permissions-updated"));
      toast.success("Role permission matrix and navigation settings saved successfully");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to save permission matrix");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredFeatures = useMemo(() => {
    if (!search.trim()) return FEATURES;
    const q = search.toLowerCase();
    return FEATURES.filter(
      (f) => f.name.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q) || f.category.toLowerCase().includes(q)
    );
  }, [search]);

  const categories = ["Core Features", "Monitoring & Protection", "Admin & Governance", "Exports & APIs"] as const;

  return (
    <div>
      {/* Info Banner */}
      <div className="matrix-info-banner">
        <Info size={20} className="text-indigo-400 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Role Permissions Matrix</strong> defines the default feature access for each role tier.
          Individual users can have per-user custom overrides. Changes saved here immediately apply to all users without explicit overrides.
        </div>
      </div>

      {/* Admin Navigation Lock vs Disable Setting Card */}
      <div className="matrix-setting-card">
        <div className="setting-card-left">
          <div className="setting-icon-box">
            <Lock size={18} style={{ color: "#f59e0b" }} />
          </div>
          <div>
            <div className="setting-card-title">Restricted Tabs Navigation Display</div>
            <div className="setting-card-subtitle">
              Choose whether tabs without user access appear with a Lock icon (🔒) or are completely disabled/hidden from sidebar.
            </div>
          </div>
        </div>
        <div className="setting-card-action">
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              background: "rgba(255, 255, 255, 0.04)",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid var(--color-border)",
            }}
          >
            <input
              type="checkbox"
              id="checkbox-show-locked-tabs"
              checked={stagedShowLocked}
              onChange={(e) => setStagedShowLocked(e.target.checked)}
              style={{
                width: "18px",
                height: "18px",
                accentColor: "var(--color-primary, #0066e0)",
                cursor: "pointer",
              }}
            />
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>
              {stagedShowLocked
                ? "Show with Lock icon (🔒)"
                : "Disable & hide restricted tabs"}
            </span>
          </label>
        </div>
      </div>

      {/* Toolbar */}
      <div className="matrix-card">
        <div className="matrix-toolbar">
          <div className="toolbar-search" style={{ maxWidth: 360 }}>
            <Search className="search-icon-pos" size={16} />
            <input
              type="text"
              placeholder="Search features or categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {diffs.length > 0 && (
              <span className="registered-count-badge" style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", borderColor: "rgba(245,158,11,0.3)" }}>
                {diffs.length} unsaved changes
              </span>
            )}
          </div>
        </div>

        {/* Matrix Header Row */}
        <div className="matrix-row" style={{ background: "var(--color-bg)", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>
          <div>Feature</div>
          <div style={{ textAlign: "center" }}>Client</div>
          <div style={{ textAlign: "center" }}>Analyst</div>
          <div style={{ textAlign: "center" }}>Admin</div>
          <div style={{ textAlign: "center" }}>Super Admin</div>
        </div>

        {/* Matrix Content */}
        {isLoading ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div className="skeleton" style={{ height: 40, width: "100%", marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 40, width: "100%", marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 40, width: "100%" }} />
          </div>
        ) : (
          categories.map((cat) => {
            const catFeatures = filteredFeatures.filter((f) => f.category === cat);
            if (catFeatures.length === 0) return null;

            return (
              <div key={cat}>
                <div className="matrix-category-header">{cat}</div>
                {catFeatures.map((feat) => {
                  const clientVal = stagedMatrix[feat.id]?.client ?? false;
                  const analystVal = stagedMatrix[feat.id]?.analyst ?? false;
                  const adminVal = stagedMatrix[feat.id]?.admin ?? false;

                  return (
                    <div key={feat.id} className="matrix-row">
                      {/* Feature Info */}
                      <div className="matrix-feature-info">
                        <div className="matrix-feature-icon">{feat.icon}</div>
                        <div>
                          <div className="matrix-feature-name">{feat.name}</div>
                          <div className="matrix-feature-desc">{feat.desc}</div>
                        </div>
                      </div>

                      {/* Client Toggle */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={clientVal}
                            disabled={feat.id === "dashboard"}
                            onChange={() => handleToggle(feat.id, "client")}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>

                      {/* Analyst Toggle */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={analystVal}
                            disabled={feat.id === "dashboard"}
                            onChange={() => handleToggle(feat.id, "analyst")}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>

                      {/* Admin Toggle */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={adminVal}
                            disabled={feat.id === "dashboard" || feat.id === "access_management" || feat.id === "admin_dashboard"}
                            onChange={() => handleToggle(feat.id, "admin")}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>

                      {/* Super Admin Column (Locked) */}
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <span className="super-admin-badge">
                          <Lock size={10} /> Full Access
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Sticky Unsaved Bar */}
      {diffs.length > 0 && (
        <div className="sticky-unsaved-bar">
          <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--color-text)" }}>
            Unsaved changes ({diffs.length})
          </span>
          <button className="btn-secondary" onClick={handleDiscard}>
            <RotateCcw size={14} /> Discard
          </button>
          <button className="btn-primary" onClick={() => setShowConfirmModal(true)}>
            <Save size={14} /> Save Matrix Changes
          </button>
        </div>
      )}

      {/* Confirm Diff Modal */}
      {showConfirmModal && (
        <div className="drawer-backdrop" style={{ justifyContent: "center", alignItems: "center" }}>
          <div
            className="matrix-card"
            style={{ width: 500, maxWidth: "90vw", padding: 24, background: "var(--color-surface)" }}
          >
            <h3 style={{ margin: "0 0 8px 0", fontSize: "1.25rem", color: "var(--color-text)" }}>
              Confirm Role Permission Matrix Changes
            </h3>
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: 16 }}>
              Review the staged permission changes before publishing to all users without custom overrides:
            </p>

            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: 8, padding: 12, marginBottom: 20 }}>
              {diffs.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderBottom: (i < diffs.length - 1) ? "1px solid var(--color-border)" : "none",
                    fontSize: "0.82rem",
                  }}
                >
                  <div>
                    <strong style={{ color: "var(--color-text)" }}>{d.feature}</strong> ({d.role.toUpperCase()})
                  </div>
                  {d.isSetting ? (
                    <div style={{ color: "#f59e0b", fontWeight: 700 }}>
                      {d.from ? "Lock Icon (🔒)" : "Hidden"} &rarr; {d.to ? "Lock Icon (🔒)" : "Hidden"}
                    </div>
                  ) : (
                    <div style={{ color: d.to ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                      {d.from ? "ENABLED" : "DISABLED"} &rarr; {d.to ? "ENABLED" : "DISABLED"}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button className="btn-secondary" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleConfirmSave} disabled={isSaving}>
                {isSaving ? "Publishing…" : "Publish Matrix"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
